import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface RoomAvailabilityRow {
  room_id: string;
  room_number: string | null;
  room_name: string | null;
  category_id: string | null;
  is_available: boolean;
  reason: 'overlap' | 'cleaning_buffer' | null;
  buffer_minutes: number | null;
  conflict_booking_id: string | null;
  conflict_reference: string | null;
  conflict_check_in: string | null;
  conflict_check_out: string | null;
  conflict_check_in_time: string | null;
  conflict_check_out_time: string | null;
  conflict_status: string | null;
}

interface Params {
  hotelId?: string | null;
  categoryId?: string | null;
  checkIn?: string | null;
  checkOut?: string | null;
  checkInTime?: string | null;
  checkOutTime?: string | null;
  excludeBookingId?: string | null;
  enabled?: boolean;
}

/** Дебаунс значения — чтобы ручной ввод дат не бил в базу на каждый символ */
function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const fmtDate = (d?: string | null) => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  } catch {
    return d;
  }
};

/** Человеческое объяснение, почему номер занят */
export function conflictText(row?: RoomAvailabilityRow | null): string | null {
  if (!row || row.is_available) return null;
  if (row.reason === 'cleaning_buffer') {
    const hours = Math.round(((row.buffer_minutes ?? 120) / 60) * 10) / 10;
    return `Уборка ${hours} ч после выезда в ${row.conflict_check_out_time || '12:00'}`;
  }
  const ref = row.conflict_reference ? `, бронь ${row.conflict_reference}` : '';
  return `Занят до ${fmtDate(row.conflict_check_out)}${ref}`;
}

/**
 * AK-47: доступность номеров считает сервер той же функцией, что и триггер защиты от двойного заселения.
 * Клиент ничего не угадывает — он только показывает вердикт сервера.
 */
export function useRoomAvailability({
  hotelId,
  categoryId,
  checkIn,
  checkOut,
  checkInTime,
  checkOutTime,
  excludeBookingId,
  enabled = true,
}: Params) {
  const debouncedIn = useDebounced(checkIn ?? null);
  const debouncedOut = useDebounced(checkOut ?? null);
  const debouncedInTime = useDebounced(checkInTime ?? null);
  const debouncedOutTime = useDebounced(checkOutTime ?? null);

  // AK-47: интервал должен быть строго положительным с учётом времени заезда/выезда
  // (те же дефолты, что на сервере: 14:00 / 12:00). Иначе RPC бросает INVALID_DATES.
  const isInvalidRange = useMemo(() => {
    if (!debouncedIn || !debouncedOut) return false;
    const arrival = new Date(`${debouncedIn}T${(debouncedInTime || '14:00').slice(0, 5)}:00`);
    const departure = new Date(`${debouncedOut}T${(debouncedOutTime || '12:00').slice(0, 5)}:00`);
    if (Number.isNaN(arrival.getTime()) || Number.isNaN(departure.getTime())) return true;
    return departure.getTime() <= arrival.getTime();
  }, [debouncedIn, debouncedOut, debouncedInTime, debouncedOutTime]);

  const isReady = Boolean(enabled && hotelId && debouncedIn && debouncedOut && !isInvalidRange);

  const query = useQuery({
    queryKey: [
      'room-availability',
      hotelId,
      categoryId ?? 'all',
      debouncedIn,
      debouncedOut,
      debouncedInTime,
      debouncedOutTime,
      excludeBookingId ?? 'new',
    ],
    enabled: isReady,
    staleTime: 30_000,
    gcTime: 60_000,
    retry: 1,
    queryFn: async (): Promise<RoomAvailabilityRow[]> => {
      const { data, error } = await supabase.rpc('get_room_availability_for_move', {
        p_hotel_id: hotelId!,
        p_category_id: categoryId || null,
        p_check_in: debouncedIn!,
        p_check_out: debouncedOut!,
        p_check_in_time: debouncedInTime || '14:00',
        p_check_out_time: debouncedOutTime || '12:00',
        p_exclude_booking_id: excludeBookingId || null,
      } as any);
      if (error) throw error;
      return (data as unknown as RoomAvailabilityRow[]) || [];
    },
  });

  const byRoomId = useMemo(() => {
    const map: Record<string, RoomAvailabilityRow> = {};
    (query.data || []).forEach((row) => {
      map[row.room_id] = row;
    });
    return map;
  }, [query.data]);

  return {
    rows: query.data || [],
    byRoomId,
    /** true — идёт проверка, показываем «Проверяем…», а не зелёную галочку */
    isChecking: isReady && (query.isLoading || query.isFetching),
    /** true — сервер недоступен, показываем «Проверка недоступна» */
    isFailed: query.isError,
    /** true — выезд не позже заезда, проверка не запускается */
    isInvalidRange,
    isReady,
    refetch: query.refetch,
  };
}
